'use client';

import Link from 'next/link';

const LAST_UPDATED = 'March 28, 2026';
const EFFECTIVE_DATE = 'March 28, 2026';
const COMPANY_NAME = 'Commercial Law Publishers (India) Pvt. Ltd.';
const APP_NAME = 'Crack AIBE';
const CONTACT_EMAIL = 'info@crackaibe.com';
const WEBSITE = 'https://crackaibe.com';
const CONTACT_PHONE = '+919899633171';
const GOVERNING_LAW = 'India';
const JURISDICTION = 'New Delhi, India';

export default function TermsAndConditionsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-gray-800 hover:text-blue-600 transition-colors">
            <img
              src="/assets/images/Logo-1774427039733.png"
              alt="Crack AIBE Logo"
              className="h-8 w-auto object-contain" />

            <span className="font-semibold text-sm hidden sm:block">{APP_NAME}</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link href="/privacy-policy" className="text-blue-600 hover:text-blue-700 transition-colors">Privacy Policy</Link>
            <Link href="/refund-policy" className="text-blue-600 hover:text-blue-700 transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Page Title */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Terms &amp; Conditions</h1>
          <p className="text-sm text-gray-500">
            <span className="font-medium">Effective Date:</span> {EFFECTIVE_DATE} &nbsp;·&nbsp;
            <span className="font-medium">Last Updated:</span> {LAST_UPDATED}
          </p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Please read these Terms &amp; Conditions carefully before using {APP_NAME}. By accessing or using our platform, you agree to be bound by these Terms. If you do not agree, please do not use the platform.
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-10">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Table of Contents</h2>
          <ol className="space-y-1.5 text-sm text-blue-600 list-decimal list-inside">
            {[
            'Introduction',
            'Acceptance of Terms',
            'Eligibility',
            'User Accounts and Responsibilities',
            'Account Security',
            'Single-Device Access and Session Restrictions',
            'Subscription, Payments, Billing, and Refunds',
            'Acceptable Use and Prohibited Conduct',
            'Intellectual Property Rights',
            'Anti-Sharing, Anti-Piracy, and Redistribution Restrictions',
            'Suspension and Termination of Accounts',
            'Disclaimer of Warranties',
            'Limitation of Liability',
            'Privacy and Data Usage',
            'Changes to These Terms',
            'Governing Law and Jurisdiction',
            'Contact Information']?.
            map((item, i) =>
            <li key={i}>
                <a href={`#section-${i + 1}`} className="hover:underline">{item}</a>
              </li>
            )}
          </ol>
        </div>

        {/* Sections */}
        <div className="space-y-10 text-gray-700 text-sm leading-relaxed">

          {/* 1 */}
          <section id="section-1">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">1. Introduction</h2>
            <p>
              Welcome to <strong>{APP_NAME}</strong>, an online legal examination preparation platform operated by <strong>{COMPANY_NAME}</strong> (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). {APP_NAME} is accessible at <a href={WEBSITE} className="text-blue-600 hover:underline">{WEBSITE}</a> and through our web application.
            </p>
            <p className="mt-3">
              {APP_NAME} provides law students with practice tests, mock examinations, topic-based question banks, performance analytics, and study tools designed to help candidates prepare for the All India Bar Examination (AIBE) conducted by the Bar Council of India.
            </p>
            <p className="mt-3">
              These Terms &amp; Conditions (&quot;Terms&quot;) govern your access to and use of the {APP_NAME} platform, including all content, features, and services offered through it. These Terms constitute a legally binding agreement between you (&quot;User&quot;, &quot;you&quot;, or &quot;your&quot;) and the Company.
            </p>
          </section>

          {/* 2 */}
          <section id="section-2">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">2. Acceptance of Terms</h2>
            <p>
              By registering for an account, accessing the platform, or making a payment, you confirm that you have read, understood, and agree to be bound by these Terms, our <Link href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>, and our <Link href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link>, all of which are incorporated herein by reference.
            </p>
            <p className="mt-3">
              If you are using the platform on behalf of an organisation or institution, you represent and warrant that you have the authority to bind that entity to these Terms, and references to &quot;you&quot; shall include that entity.
            </p>
            <p className="mt-3">
              We reserve the right to update or modify these Terms at any time. Continued use of the platform after any such changes constitutes your acceptance of the revised Terms. We will notify users of material changes via email or an in-app notice.
            </p>
          </section>

          {/* 3 */}
          <section id="section-3">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">3. Eligibility</h2>
            <p>To use {APP_NAME}, you must:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Be at least 18 years of age, or the age of majority in your jurisdiction, whichever is higher.</li>
              <li>Be a law student, law graduate, enrolled advocate, or legal professional seeking to prepare for the AIBE or related examinations.</li>
              <li>Have the legal capacity to enter into a binding contract under applicable law.</li>
              <li>Not be barred from receiving services under applicable law.</li>
              <li>Provide accurate, current, and complete information during registration.</li>
            </ul>
            <p className="mt-3">
              The Company reserves the right to refuse access to any person who does not meet these eligibility requirements or who provides false or misleading information.
            </p>
          </section>

          {/* 4 */}
          <section id="section-4">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">4. User Accounts and Responsibilities</h2>
            <p>
              To access the full features of {APP_NAME}, you must create a personal account using a valid email address. You are responsible for:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Providing accurate, truthful, and complete registration information.</li>
              <li>Keeping your account information up to date.</li>
              <li>Maintaining the confidentiality of your login credentials.</li>
              <li>All activities that occur under your account, whether or not authorised by you.</li>
              <li>Notifying us immediately at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a> if you suspect any unauthorised access to your account.</li>
            </ul>
            <p className="mt-3">
              Each account is strictly personal and non-transferable. You may not sell, transfer, sublicense, or otherwise share your account with any other person. Creating multiple accounts to circumvent subscription restrictions, access limitations, or any other platform policies is strictly prohibited.
            </p>
          </section>

          {/* 5 */}
          <section id="section-5">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">5. Account Security</h2>
            <p>
              You are solely responsible for maintaining the security of your account credentials. You must:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Use a strong, unique password that is not used for any other online service.</li>
              <li>Never share your password, OTP, or any login credentials with any other person.</li>
              <li>Log out of your account when using shared or public devices.</li>
              <li>Immediately report any suspected breach of account security to us.</li>
            </ul>
            <p className="mt-3">
              The Company will never ask you for your password via email, phone, or any other communication channel. Any such request should be treated as fraudulent and reported to us immediately.
            </p>
            <p className="mt-3">
              The Company shall not be liable for any loss or damage arising from your failure to comply with these security obligations.
            </p>
          </section>

          {/* 6 — Device/Session Restrictions */}
          <section id="section-6">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">6. Single-Device Access and Session Restrictions</h2>
            <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg mb-4">
              <p className="font-semibold text-amber-900 mb-1">⚠️ Important: One Device at a Time Policy</p>
              <p className="text-amber-800">
                Each {APP_NAME} account is licensed for use on <strong>one (1) device at a time</strong>. Simultaneous access or active usage of the same account across multiple devices is <strong>strictly prohibited</strong>.
              </p>
            </div>
            <p>
              To protect the integrity of our platform and ensure fair access for all subscribers, the following rules apply:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>
                <strong>Single active session:</strong> Your account may only be active on one device at any given time. If you log in on a second device while your account is already active on another device, one or both sessions may be automatically terminated by our system.
              </li>
              <li>
                <strong>Automatic logout enforcement:</strong> If our system detects that the same account is simultaneously active on more than one device, it may automatically log out one or both devices without prior notice as part of our account protection and access enforcement mechanisms.
              </li>
              <li>
                <strong>Account sharing is prohibited:</strong> Sharing your account credentials with any other person — whether a friend, family member, colleague, or any third party — is strictly forbidden. Each user must have their own individual account and subscription.
              </li>
              <li>
                <strong>Credential confidentiality:</strong> You must keep your login credentials strictly confidential. Disclosing your email address and password to any other person constitutes a material breach of these Terms.
              </li>
              <li>
                <strong>Bypass attempts are prohibited:</strong> Any attempt to circumvent, disable, or work around the single-device session enforcement mechanism — including but not limited to the use of VPNs, proxies, browser extensions, automation tools, or any other technical means — is strictly prohibited.
              </li>
            </ul>
            <p className="mt-4 font-semibold text-gray-900">Consequences of Violations:</p>
            <p className="mt-2">
              Violations of this section, whether intentional or repeated, may result in any or all of the following at the Company&apos;s sole discretion:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1.5 pl-2">
              <li>Immediate forced logout from all active sessions.</li>
              <li>Temporary suspension of your account.</li>
              <li>Permanent termination of your account and all associated data.</li>
              <li>Revocation of access to the platform without any refund of subscription fees paid.</li>
              <li>Legal action where applicable under Indian law.</li>
            </ul>
            <p className="mt-3">
              The Company&apos;s determination of a policy violation shall be final and binding. We reserve the right to monitor session activity, login patterns, and device fingerprints to enforce this policy.
            </p>
          </section>

          {/* 7 */}
          <section id="section-7">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">7. Subscription, Payments, Billing, and Refunds</h2>
            <p>
              Access to premium features of {APP_NAME} requires a paid subscription. The following terms apply to all subscriptions and payments:
            </p>
            <h3 className="font-semibold text-gray-900 mt-4 mb-2">7.1 Subscription Plans</h3>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Access to the platform is available for <strong>₹500 for 6 months</strong>, excluding applicable taxes.</li>
              <li>The 6-month access period begins from the date of successful payment and expires automatically after 6 months.</li>
              <li>To continue using the platform after expiry, you must renew your subscription by making a new payment.</li>
              <li>Subscription pricing is subject to change. We will provide at least 14 days&apos; notice before any price changes take effect for existing subscribers.</li>
              <li>All prices are displayed in Indian Rupees (INR) unless otherwise stated.</li>
            </ul>
            <h3 className="font-semibold text-gray-900 mt-4 mb-2">7.3 Cancellation</h3>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Your 6-month subscription is not a recurring auto-renewal plan. No automatic charges will be made after the access period ends.</li>
              <li>Once payment is made and access is granted, the subscription is considered active and no mid-term cancellation or pro-rata refund is available.</li>
              <li>Access will automatically expire at the end of the 6-month period unless you choose to renew.</li>
            </ul>
            <h3 className="font-semibold text-gray-900 mt-4 mb-2">7.4 Refunds</h3>
            <p>
              Please refer to our <Link href="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link> for full details. In summary:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-2 pl-2">
              <li>All payments are <strong>non-refundable</strong>. There is no automatic or standard refund for the 6-month subscription.</li>
              <li>Refunds may be considered strictly on a <strong>case-by-case basis</strong> at the sole discretion of the Company.</li>
              <li>To request a refund, you must contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a> and explain your case in detail. We will review each request individually.</li>
              <li>Accounts terminated due to violations of these Terms are not eligible for any refund under any circumstances.</li>
            </ul>
          </section>

          {/* 9 */}
          <section id="section-9">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">9. Acceptable Use and Prohibited Conduct</h2>
            <p>You agree to use {APP_NAME} only for lawful purposes and in accordance with these Terms. You must not:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Use the platform for any unlawful, fraudulent, or malicious purpose.</li>
              <li>Reproduce, copy, distribute, sell, or commercially exploit any content from the platform without our prior written consent.</li>
              <li>Use automated tools, bots, scrapers, crawlers, or scripts to access, extract, or index any content from the platform.</li>
              <li>Attempt to gain unauthorised access to any part of the platform, its servers, databases, or related systems.</li>
              <li>Interfere with or disrupt the integrity or performance of the platform or its infrastructure.</li>
              <li>Upload, transmit, or distribute any viruses, malware, or other harmful code.</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with any person or entity.</li>
              <li>Engage in any conduct that restricts or inhibits any other user&apos;s use or enjoyment of the platform.</li>
              <li>Attempt to reverse-engineer, decompile, or disassemble any part of the platform.</li>
              <li>Use the platform to collect or harvest personal data of other users.</li>
              <li>Violate any applicable local, national, or international law or regulation.</li>
            </ul>
          </section>

          {/* 10 */}
          <section id="section-10">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">10. Intellectual Property Rights</h2>
            <p>
              All content on {APP_NAME}, including but not limited to text, questions, answer explanations, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, is the exclusive property of {COMPANY_NAME} or its content suppliers and is protected by Indian and international copyright, trademark, and intellectual property laws.
            </p>
            <p className="mt-3">
              You are granted a limited, non-exclusive, non-transferable, revocable licence to access and use the platform solely for your personal, non-commercial educational purposes. This licence does not include:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Any resale or commercial use of the platform or its content.</li>
              <li>Any collection and use of product listings, descriptions, or prices.</li>
              <li>Any derivative use of the platform or its content.</li>
              <li>Any downloading or copying of account information for the benefit of another merchant.</li>
              <li>Any use of data mining, robots, or similar data gathering and extraction tools.</li>
            </ul>
            <p className="mt-3">
              Any unauthorised use terminates the licence granted by the Company. All rights not expressly granted in these Terms are reserved by the Company.
            </p>
          </section>

          {/* 11 */}
          <section id="section-11">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">11. Anti-Sharing, Anti-Piracy, and Redistribution Restrictions</h2>
            <p>
              The content available on {APP_NAME} — including all practice questions, mock tests, answer keys, explanations, and study materials — is proprietary and confidential. The following restrictions apply strictly:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>
                <strong>No redistribution:</strong> You may not reproduce, republish, upload, post, transmit, or distribute any content from the platform in any form or by any means, including electronic, mechanical, photocopying, recording, or otherwise.
              </li>
              <li>
                <strong>No screen recording or screenshots for distribution:</strong> Taking screenshots or screen recordings of platform content for the purpose of sharing, distributing, or publishing is strictly prohibited.
              </li>
              <li>
                <strong>No sharing of questions or answers:</strong> Sharing, posting, or transmitting platform questions, answers, or explanations on social media, messaging apps, forums, websites, or any other medium is a violation of our intellectual property rights.
              </li>
              <li>
                <strong>No commercial exploitation:</strong> You may not use any platform content for commercial purposes, including tutoring, coaching, or selling study materials derived from our content.
              </li>
              <li>
                <strong>No scraping:</strong> Automated extraction of content using any technical means is strictly prohibited and constitutes a violation of both these Terms and applicable law.
              </li>
            </ul>
            <p className="mt-3">
              Violations of this section may result in immediate account termination, civil legal action for damages, and/or criminal prosecution under applicable Indian law, including the Copyright Act, 1957 and the Information Technology Act, 2000.
            </p>
          </section>

          {/* 12 */}
          <section id="section-12">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">12. Suspension and Termination of Accounts</h2>
            <p>
              The Company reserves the right to suspend, restrict, or permanently terminate your account and access to the platform at any time, with or without notice, for any of the following reasons:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Violation of any provision of these Terms.</li>
              <li>Sharing account credentials or allowing simultaneous multi-device access.</li>
              <li>Fraudulent, abusive, or illegal activity.</li>
              <li>Non-payment of subscription fees.</li>
              <li>Providing false or misleading information during registration.</li>
              <li>Any conduct that the Company, in its sole discretion, determines to be harmful to the platform, other users, or the Company.</li>
            </ul>
            <p className="mt-3">
              Upon termination, your right to access the platform ceases immediately. The Company shall not be liable to you or any third party for any termination of your access. Accounts terminated for policy violations are not eligible for refunds.
            </p>
            <p className="mt-3">
              You may terminate your account at any time by contacting us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>. Termination of your account does not relieve you of any obligations incurred prior to termination.
            </p>
          </section>

          {/* 13 */}
          <section id="section-13">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">13. Disclaimer of Warranties</h2>
            <p>
              {APP_NAME} is provided on an &quot;as is&quot; and &quot;as available&quot; basis without any warranties of any kind, either express or implied. To the fullest extent permitted by applicable law, the Company expressly disclaims all warranties, including but not limited to:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
              <li>Warranties that the platform will be uninterrupted, error-free, or free of viruses or other harmful components.</li>
              <li>Warranties regarding the accuracy, completeness, reliability, or timeliness of any content on the platform.</li>
              <li>Warranties that the platform will meet your specific requirements or expectations.</li>
            </ul>
            <p className="mt-3">
              The Company does not guarantee that use of the platform will result in passing the AIBE or any other examination. The platform is a study aid only and does not constitute legal advice or professional guidance.
            </p>
          </section>

          {/* 14 */}
          <section id="section-14">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">14. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, in no event shall {COMPANY_NAME}, its directors, officers, employees, agents, partners, or licensors be liable for any:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Indirect, incidental, special, consequential, or punitive damages.</li>
              <li>Loss of profits, revenue, data, goodwill, or other intangible losses.</li>
              <li>Damages resulting from your access to or use of (or inability to access or use) the platform.</li>
              <li>Damages resulting from any unauthorised access to or alteration of your transmissions or data.</li>
              <li>Damages resulting from any content obtained from the platform.</li>
            </ul>
            <p className="mt-3">
              In any case, the Company&apos;s aggregate liability to you for all claims arising out of or relating to these Terms or your use of the platform shall not exceed the total amount paid by you to the Company in the twelve (12) months immediately preceding the event giving rise to the claim.
            </p>
          </section>

          {/* 15 */}
          <section id="section-15">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">15. Privacy and Data Usage</h2>
            <p>
              Your privacy is important to us. Our collection, use, and disclosure of your personal information is governed by our <Link href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.
            </p>
            <p className="mt-3">
              By using {APP_NAME}, you consent to the collection and use of your information as described in our Privacy Policy. Key points include:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>We collect information you provide during registration and use of the platform.</li>
              <li>We use your data to provide, improve, and personalise the platform experience.</li>
              <li>We do not sell your personal data to third parties.</li>
              <li>We implement industry-standard security measures to protect your data.</li>
              <li>We may use session and device data to enforce our single-device access policy.</li>
            </ul>
          </section>

          {/* 16 */}
          <section id="section-16">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">16. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we make material changes, we will:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 pl-2">
              <li>Update the &quot;Last Updated&quot; date at the top of this page.</li>
              <li>Send a notification to the email address associated with your account.</li>
              <li>Display a prominent notice on the platform.</li>
            </ul>
            <p className="mt-3">
              Your continued use of the platform after the effective date of any changes constitutes your acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the platform and cancel your subscription.
            </p>
          </section>

          {/* 17 */}
          <section id="section-17">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">17. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of <strong>{GOVERNING_LAW}</strong>, without regard to its conflict of law provisions.
            </p>
            <p className="mt-3">
              Any dispute, controversy, or claim arising out of or relating to these Terms, or the breach, termination, or invalidity thereof, shall be subject to the exclusive jurisdiction of the courts located in <strong>{JURISDICTION}</strong>. You hereby consent to the personal jurisdiction of such courts and waive any objection to the laying of venue in such courts.
            </p>
            <p className="mt-3">
              Before initiating any legal proceedings, you agree to first attempt to resolve any dispute informally by contacting us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>. We will make reasonable efforts to resolve the dispute within 30 days.
            </p>
          </section>

          {/* 18 */}
          <section id="section-18">
            <h2 className="text-xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">18. Contact Information</h2>
            <p>
              If you have any questions, concerns, or complaints regarding these Terms or the platform, please contact us:
            </p>
            <div className="mt-4 p-5 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <p><span className="font-semibold text-gray-900">Company:</span> {COMPANY_NAME}</p>
              <p><span className="font-semibold text-gray-900">Platform:</span> {APP_NAME}</p>
              <p>
                <span className="font-semibold text-gray-900">Email:</span>{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">{CONTACT_EMAIL}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Phone / WhatsApp:</span>{' '}
                <a href={`tel:${CONTACT_PHONE}`} className="text-blue-600 hover:underline">{CONTACT_PHONE}</a>
              </p>
              <p>
                <span className="font-semibold text-gray-900">Website:</span>{' '}
                <a href={WEBSITE} className="text-blue-600 hover:underline">{WEBSITE}</a>
              </p>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              We aim to respond to all queries within 2–3 business days.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <p>© {new Date()?.getFullYear()} {COMPANY_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link href="/refund-policy" className="hover:text-gray-600 transition-colors">Refund Policy</Link>
            <Link href="/login" className="hover:text-gray-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </div>
    </div>);

}